import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProductById, getProductWithCategory, getCategoryById } from '../../api/productService';
import CustomerReviews from './CustomerReviews';
import ProductEnquiryForm from './ProductEnquiryForm';
import useEmblaCarousel from 'embla-carousel-react';
import Seo from '../common/Seo';
import { pushToDataLayer } from '../../utils/gtm';

const ProductDetails = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    howToUse: true,
    formulation: false,
    modeOfAction: false,
    crops: false,
    reviews: false
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getPrimaryImageUrl = () => {
    if (!product?.images) return '';

    let imageUrl = '';

    if (typeof product.images === 'string') {
      imageUrl = product.images;
    } else if (Array.isArray(product.images) && product.images.length > 0) {
      imageUrl = typeof product.images[0] === 'string' ? product.images[0] : '';
    }

    if (!imageUrl || typeof imageUrl !== 'string') return '';

    return imageUrl.startsWith('http')
      ? imageUrl
      : `${process.env.REACT_APP_API_URL}/${imageUrl}`;
  };

  // Handle carousel selection
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect(); // Set initial state

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      try {
        // First, get the product data
        const data = await getProductById(id);
        
        // Extract product from response
        let productData = data.product || data.data || data;
        setProduct(productData);
        
        // Check if product has categories array or category_id
        if (productData.categories && Array.isArray(productData.categories) && productData.categories.length > 0) {
          const firstCategory = productData.categories[0];
          const categoryToSet = {
            category_name: firstCategory.category_name,
            name: firstCategory.category_name,
            title: firstCategory.category_name,
            _id: firstCategory._id
          };
          setCategory(categoryToSet);
          return; // Exit early to prevent fallback logic from overriding
        } else {
          // Old structure: check for category_id and fetch category details
          const categoryId = productData.category_id || productData.categoryId;
          if (categoryId) {
            try {
              const categoryData = await getCategoryById(categoryId);
              
              // Extract category from response
              const categoryInfo = categoryData.category || categoryData.data || categoryData;
              
              // If category has product_name instead of category_name, use it
              if (categoryInfo.product_name && !categoryInfo.category_name) {
                categoryInfo.category_name = categoryInfo.product_name;
              }
              
              setCategory(categoryInfo);
              return; // Exit early to prevent fallback logic from overriding
            } catch (categoryError) {
              // If category fetch fails, try to use category name from product if available
              if (productData.category || productData.category_name) {
                setCategory({
                  category_name: productData.category || productData.category_name,
                  name: productData.category || productData.category_name,
                  title: productData.category || productData.category_name
                });
              } else if (productData.product_name) {
                // If no category field but product_name exists, use it as category
                setCategory({
                  category_name: productData.product_name,
                  name: productData.product_name,
                  title: productData.product_name,
                  product_name: productData.product_name
                });
              } else {
                // Set a default category if none is available
                setCategory({
                  category_name: 'General',
                  name: 'General',
                  title: 'General'
                });
              }
            }
          }
        }
        
        if (productData.category || productData.category_name) {
          // If no category_id but category name exists, use it directly
          const fallbackCategory = {
            category_name: productData.category || productData.category_name,
            name: productData.category || productData.category_name,
            title: productData.category || productData.category_name
          };
          setCategory(fallbackCategory);
        } else if (productData.category_name) {
          // If no category field but product_name exists, use it as category
          setCategory({
            category_name: productData.category_name,
            name: productData.category_name,
            title: productData.category_name,
            product_name: productData.product_name
          });
        } else {
          // Set a default category if no category information is available
          setCategory({
            category_name: 'General',
            name: 'General',
            title: 'General'
          });
        }
        
      } catch (err) {
        setError('Failed to load product details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [id]);

  useEffect(() => {
    // Only push when we have the actual product data ready
    if (loading || error || !product) return;
  
    pushToDataLayer({
      event: 'seo_product_view', // Simple event name for your GTM trigger
      product_data: {
        name: product.product_name || '',
        id: product._id || product.id || id,
        category: category?.category_name || 'All Products',
        description: product.description || '',
        image: getPrimaryImageUrl()
      }
    });
  }, [loading, error, product, category, id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error || 'Product not found'}</p>
          <Link to="/products" className="text-green-600 hover:text-green-800 mt-2 inline-block">
            Return to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom py-[40px] md:py-[80px] gap-[66px] bg-white">
      {product && (
        <Seo
          title={`${product.product_name} | Agastya Agro Limited`}
          description={
            product.description
              ? `${product.description} Learn how it works, where to use it, and request expert guidance from Agastya Agro Limited.`
              : `Discover ${product.product_name} from Agastya Agro Limited with use guidance, formulation details, and crop-specific recommendations.`
          }
          type="product"
        />
      )}
      {/* Back button and Breadcrumbs */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-4 md:mb-8 opacity-60">
        <div className="flex items-center text-sm text-gray-600">
          <Link to="/" className="transition-colors duration-200 font-medium">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/products" className="transition-colors duration-200 font-medium">Categories</Link>
          {category && category.category_name && category.category_name !== 'General' && (
            <>
              <span className="mx-2 text-gray-400">/</span>
              <Link 
                to={`/products/${(category.category_name).toLowerCase().replace(/\s+/g, '-')}`} 
                className="hover:text-gray-600 transition-colors duration-200 font-medium"
              >
                {category.category_name}
              </Link>
            </>
          )}
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-800 font-semibold">{product.product_name}</span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-[30px] lg:gap-16">
        {/* Product Images with Embla Carousel */}
        <div className="lg:w-[610px]">
          <div className="relative bg-white rounded-[16px] overflow-hidden h-[358px] md:h-[610px] shadow-sm">
            {product.images ? (
              Array.isArray(product.images) && product.images.length > 1 ? (
                <>
                  <div className="embla h-full" ref={emblaRef}>
                    <div className="embla__container h-full flex">
                      {product.images.map((image, index) => (
                        <div key={index} className="embla__slide flex-[0_0_100%] min-w-0">
                          <div className="h-full w-full flex items-center justify-center">
                            <img 
                              src={typeof image === 'string' && image.startsWith('http') ? image : `${process.env.REACT_APP_API_URL}/${image}`} 
                              alt={`${product.product_name} ${index + 1}`} 
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Pagination Dots - Overlay on Image */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex justify-center items-center space-x-2 z-10">
                    {product.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => emblaApi?.scrollTo(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-200 ${
                          selectedIndex === index 
                            ? 'w-8 h-2 bg-white rounded-full' 
                            : 'w-2 h-2 bg-white bg-opacity-50 rounded-full hover:bg-opacity-75'
                        }`}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <img 
                    src={Array.isArray(product.images) && product.images.length > 0 
                      ? (typeof product.images[0] === 'string' && product.images[0].startsWith('http') 
                          ? product.images[0] 
                          : `${process.env.REACT_APP_API_URL}/${product.images[0]}`)
                      : ''}
                    alt={product.product_name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <span className="text-gray-400">No image available</span>
              </div>
            )}
          </div>
        </div>

        {/* Product Details */}
        <div className="flex-1 bg-white">
          {/* Product Title Box */}
          <div className="mb-[40px] md:mb-[30px]">
            <div className="mb-[30px]">
              <h1 className="text-2xl font-bold text-gray-800 mb-[20px] font-[LTWave] ">
                {product.product_name || 'No product name'}
                {category && (category.category_name !== 'General' || category.product_name !== 'General') && (
                  <span className="text-2xl font-bold text-gray-800 ml-2 font-[LTWave]">- (
                    {category.category_name || category.name || category.title})
                  </span>
                )}
              </h1>
              <p className="text-gray-600">{product.description}</p> {/* product.title */}
            </div>
            <div className="flex justify-center w-full">
              <button 
                onClick={() => setShowEnquiryForm(true)} 
                className="w-full py-4 rounded-[12px] uppercase font-medium transition-all duration-300 text-center text-sm text-white
                bg-gradient-to-l from-[#FFB603] via-[#DB4B12] to-[#593747] hover:brightness-110 hover:scale-[1.01]
                md:bg-none md:bg-black md:hover:bg-gray-800"
              >
                Enquire Now
              </button>

            </div>
          </div>
          
          {/* Expandable Sections */}
          {/* How to use */}
          <div className="border-t border-gray-200">
            <button 
              onClick={() => toggleSection('howToUse')} 
              className="w-full flex items-center justify-between py-4 px-1 focus:outline-none"
            >
              <h2 className="text-lg font-medium">How to use</h2>
              <span className="text-xl font-bold">{expandedSections.howToUse ? '−' : '+'}</span>
            </button>
            {expandedSections.howToUse && (
              <div className="pb-4 px-1">
                <p className="text-gray-600">
                  {product.usage_instructions || 
                    "Apply insecticide X as a foliar spray using ground or aerial equipment. Ensure thorough coverage of plant surfaces. For best results, apply at the first sign of pest infestation or when pest populations reach economic thresholds. Refer to the product label for specific application rates and timing for different crops and pests."}
                </p>
              </div>
            )}
          </div>
          
          {/* Formulation */}
          <div className="border-t border-gray-200">
            <button 
              onClick={() => toggleSection('formulation')} 
              className="w-full flex items-center justify-between py-4 px-1 focus:outline-none"
            >
              <h2 className="text-lg font-medium">Formulation</h2>
              <span className="text-xl font-bold">{expandedSections.formulation ? '−' : '+'}</span>
            </button>
            {expandedSections.formulation && (
              <div className="pb-4 px-1">
                <p className="text-gray-600">
                  {product.formulation || 
                    "Water-soluble liquid concentrate formulation containing active ingredient at 50% w/v."}
                </p>
              </div>
            )}
          </div>
          
          {/* Mode of Action */}
          <div className="border-t border-gray-200">
            <button 
              onClick={() => toggleSection('modeOfAction')} 
              className="w-full flex items-center justify-between py-4 px-1 focus:outline-none"
            >
              <h2 className="text-lg font-medium">Mode of Action</h2>
              <span className="text-xl font-bold">{expandedSections.modeOfAction ? '−' : '+'}</span>
            </button>
            {expandedSections.modeOfAction && (
              <div className="pb-4 px-1">
                <p className="text-gray-600">
                  {product.mode_of_action || 
                    "Acts on the nervous system of target pests, disrupting neural transmission and causing paralysis and death. Provides both contact and systemic activity."}
                </p>
              </div>
            )}
          </div>
          
          {/* Crops */}
          <div className="border-t border-b border-gray-200">
            <button 
              onClick={() => toggleSection('crops')} 
              className="w-full flex items-center justify-between py-4 px-1 focus:outline-none"
            >
              <h2 className="text-lg font-medium">Crops</h2>
              <span className="text-xl font-bold">{expandedSections.crops ? '−' : '+'}</span>
            </button>
            {expandedSections.crops && (
              <div className="pb-4 px-1">
                <p className="text-gray-600">
                  {product.crops || 
                    "Registered for use on a wide range of crops including cotton, rice, vegetables, fruits, and ornamentals."}
                </p>
              </div>
            )}
          </div>
          
          {/* Customer Reviews Section */}
          <CustomerReviews />
          
          {/* Safety Information if available */}
          {product.safety_info && (
            <div className="border-t border-gray-200">
              <button 
                onClick={() => toggleSection('safety')} 
                className="w-full flex items-center justify-between py-4 px-1 focus:outline-none"
              >
                <h2 className="text-lg font-medium">Safety Information</h2>
                <span className="text-xl font-bold">{expandedSections.safety ? '−' : '+'}</span>
              </button>
              {expandedSections.safety && (
                <div className="pb-4 px-1">
                  <p className="text-gray-600">{product.safety_info}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Product Enquiry Form Modal */}
      {showEnquiryForm && product && (
        <ProductEnquiryForm 
          productId={product._id || product.id} 
          productName={product.product_name} 
          onClose={() => setShowEnquiryForm(false)} 
        />
      )}
    </div>
  );
};

export default ProductDetails;
